import { useEffect, useState, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router';
import { getDaysBetweenDates, formatDate, FORMAT } from '../../../lib/date';
import { ReactComponent as PlaySvg } from '../../../assets/svg/play.svg';
import { ReactComponent as CrownSvg } from '../../../assets/svg/crown.svg';
import { SparklesIcon } from '@heroicons/react/20/solid';
import { DASHBOARD, NAVIGATION } from '../../../types/enum';
import request from '../../../lib/request';
import PayDialog from '../../../components/premium/payDialog';
import ChallengePopup from './challengePopup';
import PackPopup from './packPopup';
import StartPopup from './startPopup';
import AllChallenges from './allChallenges';
import ContentPopup from './contentPopup';

const ListView = ({ title, list, handleClickChallenge }) => {
  return (
    list &&
    list.length > 0 && (
      <div className='relative'>
        <h2 className='text-white font-bold px-4 mb-2'>{title}</h2>
        <div className='relative flex items-center justify-start gap-2 overflow-x-auto px-4 z-10 scrollbar-hidden'>
          {list.map((item) => (
            <div
              key={item.id}
              className='flex-shrink-0 aspect-[2/3] w-[20vw] min-w-[108px] max-w-[140px] relative'
              onClick={handleClickChallenge(item.challengeId)}
            >
              <img
                src={item.bannerUrl}
                alt='poster'
                className='object-cover object-center w-full h-full rounded-md'
              />
              {item.premium && (
                <div className='absolute top-0 left-0 bg-accent px-1 py-[1px] rounded-tl-md rounded-br-md'>
                  <CrownSvg className='size-3' />
                </div>
              )}
              {item.startDate ? (
                new Date(item.startDate) <= new Date() ? (
                  <div className='absolute bottom-0 right-0 bg-carbs/60 px-1 py-[1px] rounded-tl-md rounded-br-md text-xs text-white'>
                    Day{' '}
                    {getDaysBetweenDates(new Date(item.startDate), new Date()) +
                      1}
                  </div>
                ) : (
                  <div className='absolute bottom-0 right-0 bg-carbs/50 px-1 py-[1px] rounded-tl-md rounded-br-md text-xs text-white'>
                    {formatDate(new Date(item.startDate), FORMAT.MONTHDATE)}
                  </div>
                )
              ) : null}
            </div>
          ))}
        </div>
      </div>
    )
  );
};

const CollectionListView = ({
  title,
  list,
  handleClickChallenge,
  handleClickPack,
  handleClickContent,
}) => {
  return (
    list &&
    list.length > 0 && (
      <div className='relative'>
        <h2 className='text-white font-bold px-4 mb-2'>{title}</h2>
        <div className='relative flex items-center justify-start gap-2 overflow-x-auto px-4 z-10 scrollbar-hidden'>
          {list.map((item, i) => (
            <div
              key={i}
              className='flex-shrink-0 aspect-[2/3] w-[20vw] min-w-[108px] max-w-[140px] relative'
              onClick={
                item.challenge
                  ? handleClickChallenge(item.challenge.id)
                  : item.pack
                  ? handleClickPack(item.pack.id)
                  : item.content
                  ? handleClickContent(item.content.id)
                  : null
              }
            >
              <img
                src={
                  item.challenge
                    ? item.challenge.bannerUrl
                    : item.pack
                    ? item.pack.bannerUrl
                    : item.content
                    ? item.content.bannerUrl
                    : ''
                }
                alt='poster'
                className='object-cover object-center w-full h-full rounded-md'
              />
              {item.challenge && item.challenge.premium && (
                <div className='absolute top-0 left-0 bg-accent px-1 py-[1px] rounded-tl-md rounded-br-md'>
                  <CrownSvg className='size-3' />
                </div>
              )}
              {item.challenge &&
                (item.challenge.startDate ? (
                  new Date(item.challenge.startDate) <= new Date() ? (
                    <div className='absolute bottom-0 right-0 bg-carbs/60 px-1 py-[1px] rounded-tl-md rounded-br-md text-xs text-white'>
                      Day{' '}
                      {getDaysBetweenDates(
                        new Date(item.challenge.startDate),
                        new Date()
                      ) + 1}
                    </div>
                  ) : (
                    <div className='absolute bottom-0 right-0 bg-carbs/50 px-1 py-[1px] rounded-tl-md rounded-br-md text-xs text-white'>
                      {formatDate(
                        new Date(item.challenge.startDate),
                        FORMAT.MONTHDATE
                      )}
                    </div>
                  )
                ) : null)}
              {item.content && (
                <div className='absolute bottom-0 right-0 bg-carbs/50 px-1 py-[1px] rounded-tl-md rounded-br-md text-xs text-white'>
                  Read more
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  );
};

const Challenge = () => {
  const { user, refreshUser, handleCurrentNav, handleDashboardSection } =
    useOutletContext();
  const navigate = useNavigate();

  const challengePopupRef = useRef(null);
  const packPopupRef = useRef(null);
  const startPopupRef = useRef(null);
  const payDialogRef = useRef(null);
  const contentPopupRef = useRef(null);

  const [recommend, setRecommend] = useState(null);
  const [userChallenges, setUserChallenges] = useState([]);

  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [challengePopupOpen, setChallengePopupOpen] = useState(false);
  const [packPopupOpen, setPackPopupOpen] = useState(false);
  const [collections, setCollections] = useState([]);
  const [selectedChallengeId, setSelectedChallengeId] = useState(null);
  const [selectedPackId, setSelectedPackId] = useState(null);
  const [selectedContentId, setSelectedContentId] = useState(null);
  const [contentPopupOpen, setContentPopupOpen] = useState(false);

  useEffect(() => {
    getUserChallengeList();
    getChallengeCollection();
  }, []);

  const getChallengeCollection = async () => {
    try {
      const { result } = await request.get('/challenge/collection');
      if (result) {
        const visibleResult = result.filter((item) => item.hidden === false);
        setCollections(visibleResult);

        const list = [];

        result.forEach((item) => {
          if (item.mainBanner) {
            item.challenges.forEach(({ challenge }) => {
              if (challenge) {
                list.push(challenge);
              }
            });
          }
        });

        const randomIndex = Math.floor(Math.random() * list.length);
        setRecommend(list[randomIndex]);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const goToDashboardChallenge = () => {
    navigate('/dashboard');
    handleCurrentNav(NAVIGATION.DASHBOARD)();
    handleDashboardSection(DASHBOARD.CHALLENGE)();
  };

  const getUserChallengeList = async () => {
    try {
      const currentList = await request.get(
        `/user/challenges?status=CURRENT&localDate=${formatDate(new Date())}`
      );
      const scheduledList = await request.get(
        `/user/challenges?status=SCHEDULED&localDate=${formatDate(new Date())}`
      );

      if (currentList?.result && scheduledList?.result) {
        setUserChallenges([
          ...currentList.result.map((item) => ({
            ...item,
            enrolled: true,
          })),
          ...scheduledList.result.map((item) => ({
            ...item,
            enrolled: true,
          })),
        ]);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const openChallengePopup = () => {
    if (challengePopupRef.current) {
      challengePopupRef.current.showModal();
      setChallengePopupOpen(true);
    }
  };

  const closeChallengePopup = () => {
    if (challengePopupRef.current) {
      challengePopupRef.current.close();
      setSelectedChallengeId(null);
      setChallengePopupOpen(false);
    }
  };

  const openPackPopup = () => {
    if (packPopupRef.current) {
      packPopupRef.current.showModal();
      setPackPopupOpen(true);
    }
  };

  const closePackPopup = () => {
    if (packPopupRef.current) {
      packPopupRef.current.close();
      setSelectedPackId(null);
      setPackPopupOpen(false);
    }
  };

  const openStartPopup = (challenge) => {
    if (startPopupRef.current) {
      startPopupRef.current.showModal();
      setSelectedChallenge(challenge);
    }
  };

  const closeStartPopup = () => {
    if (startPopupRef.current) {
      startPopupRef.current.close();
      setSelectedChallenge(null);
    }
  };

  const openPayDialog = () => {
    if (payDialogRef.current) {
      payDialogRef.current.showModal();
    }
  };

  const closePayDialog = () => {
    if (payDialogRef.current) {
      payDialogRef.current.close();
    }
  };

  const openContentPopup = () => {
    if (contentPopupRef.current) {
      contentPopupRef.current.showModal();
      setContentPopupOpen(true);
    }
  };

  const closeContentPopup = () => {
    if (contentPopupRef.current) {
      contentPopupRef.current.close();
      setSelectedContentId(null);
      setContentPopupOpen(false);
    }
  };

  const handleClickChallenge = (id) => () => {
    setSelectedChallengeId(id);
    openChallengePopup();
  };

  const handleClickPack = (id) => () => {
    setSelectedPackId(id);
    openPackPopup();
  };

  const handleClickContent = (id) => () => {
    setSelectedContentId(id);
    openContentPopup();
  };

  return (
    <div className={`bg-secondary min-h-screen pb-24 scrollbar-hidden`}>
      <div className='relative w-full'>
        <img
          src={
            recommend
              ? recommend.bannerUrl
              : 'https://static-resource.thechamps.app/logo/563x563.png'
          }
          alt='recommend'
          className='w-full object-cover object-top'
        />
        <div className='absolute bottom-0 w-full h-24 bg-gradient-to-t from-secondary to-transparent' />
        {recommend && (
          <div
            className='absolute bottom-12 w-full text-white text-center flex justify-center items-center gap-4 px-12'
            style={{ fontFamily: "'Teko', sans-serif" }}
          >
            <button
              className='bg-white text-black rounded-md px-4 pt-2 pb-1 flex items-center '
              onClick={handleClickChallenge(recommend.id)}
            >
              <PlaySvg className='size-6' />
              <span className='text-xl'>START</span>
            </button>
          </div>
        )}
      </div>
      <div className='space-y-4'>
        <ListView
          title='My Challenges'
          list={userChallenges}
          handleClickChallenge={handleClickChallenge}
        />
        {collections.map((collection) => (
          <CollectionListView
            key={collection.id}
            title={collection.title}
            list={collection.challenges}
            handleClickChallenge={handleClickChallenge}
            handleClickPack={handleClickPack}
            handleClickContent={handleClickContent}
          />
        ))}
        {user?.activated || user?.programRegistered ? (
          <AllChallenges handleClickChallenge={handleClickChallenge} />
        ) : null}
      </div>
      {!user?.activated && !user?.programRegistered && (
        <div className='space-y-4'>
          <div className='relative max-h-[640px] overflow-hidden'>
            <div className='space-y-4 blur-[8px]'>
              {collections.map((collection) => (
                <CollectionListView
                  key={collection.id}
                  title={collection.title}
                  list={collection.challenges}
                  handleClickChallenge={handleClickChallenge}
                  handleClickPack={handleClickPack}
                  handleClickContent={handleClickContent}
                />
              ))}
            </div>
            <div className='absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-secondary to-transparent' />
            <div className='absolute -bottom-1 left-0 w-full h-full bg-gradient-to-t from-secondary to-transparent' />
            <div className='absolute top-48 left-0 flex flex-col items-center justify-center w-full'>
              <div className='flex flex-col items-center justify-center gap-2'>
                <p className='text-center italic text-transparent bg-clip-text bg-gradient-to-tr from-white to-accent'>
                  Ready to build
                  <br />
                  unstoppable momentum?
                </p>
                <button
                  className='flex items-center justify-center gap-2 text-black text-center font-bold bg-accent px-4 py-2 rounded-full'
                  onClick={openPayDialog}
                >
                  <SparklesIcon className='size-6' />
                  <span>Unlock all challenges</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <PackPopup
        dialogRef={packPopupRef}
        closeDialog={closePackPopup}
        handleClickChallenge={handleClickChallenge}
        selectedPackId={selectedPackId}
        packPopupOpen={packPopupOpen}
      />
      <ChallengePopup
        dialogRef={challengePopupRef}
        closeDialog={closeChallengePopup}
        openStartPopup={openStartPopup}
        goToDashboardChallenge={goToDashboardChallenge}
        selectedChallengeId={selectedChallengeId}
        challengePopupOpen={challengePopupOpen}
      />
      <StartPopup
        dialogRef={startPopupRef}
        closeDialog={closeStartPopup}
        selectedChallenge={selectedChallenge}
        user={user}
        openPayDialog={openPayDialog}
        closeChallengePopup={closeChallengePopup}
        goToDashboardChallenge={goToDashboardChallenge}
      />
      <ContentPopup
        dialogRef={contentPopupRef}
        closeDialog={closeContentPopup}
        contentId={selectedContentId}
        contentPopupOpen={contentPopupOpen}
      />
      <PayDialog
        dialogRef={payDialogRef}
        closeDialog={closePayDialog}
        title='Go Premium'
        refreshUser={refreshUser}
      />
    </div>
  );
};

export default Challenge;
